import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useState, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMonitorLatency, LatencyBucket } from "../lib/api";

interface LatencyChartProps {
  monitorId: string;
  title?: string;
  timezone?: string;
  latencyThreshold?: number | null;
}

type TimeRange = "1h" | "24h" | "7d" | "30d" | "custom";

const PSTAT_INFO = {
  p50: "Median latency — half of your regions responded faster than this",
  p95: "95th percentile — only 5% of regions were slower than this",
  p99: "99th percentile — worst-case latency across all regions",
};

const MIN_AGE_HOURS: Record<Exclude<TimeRange, "custom">, number> = {
  "1h":  0,
  "24h": 0,
  "7d":  24,
  "30d": 7 * 24,
};

function formatTick(iso: string, range: TimeRange, customSince: string, customUntil: string, tz: string): string {
  const d = new Date(iso);
  if (range === "custom") {
    const widthMs = customUntil && customSince
      ? new Date(customUntil).getTime() - new Date(customSince).getTime()
      : 24 * 3600 * 1000;
    if (widthMs <= 24 * 3600 * 1000) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZone: tz });
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric", timeZone: tz });
  }
  if (range === "1h" || range === "24h") {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZone: tz });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric", timeZone: tz });
}

function tickInterval(range: TimeRange, bucketCount: number): number | "preserveStartEnd" {
  if (range === "1h") return 0;
  if (range === "24h") return Math.max(0, Math.floor(bucketCount / 12) - 1);
  if (range === "7d") return 7;
  if (range === "custom") return Math.max(0, Math.floor(bucketCount / 8) - 1);
  return Math.max(0, Math.floor(bucketCount / 10) - 1);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomDot(props: any) {
  const { cx, cy, payload, avgP50, latencyThreshold } = props;
  if (cx == null || cy == null) return null;

  const hasError = (payload as LatencyBucket).error_count > 0;
  const slow = !hasError && payload.p50 != null && (
    latencyThreshold != null
      ? payload.p50 > latencyThreshold
      : avgP50 != null && payload.p50 > avgP50 * 2.5
  );

  if (hasError) {
    return <circle cx={cx} cy={cy} r={5} fill="#FF5C5C" stroke="#121212" strokeWidth={2} />;
  }
  if (slow) {
    return <circle cx={cx} cy={cy} r={4} fill="#FDDD48" stroke="#121212" strokeWidth={2} />;
  }
  return null;
}

function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(val: string): string {
  return new Date(val).toISOString();
}

export default function LatencyChart({ monitorId, title = "Latency History", timezone = "UTC", latencyThreshold }: LatencyChartProps) {
  const [range, setRange] = useState<TimeRange>("24h");
  const [showInfo, setShowInfo] = useState<keyof typeof PSTAT_INFO | null>(null);

  const nowRef = useRef(new Date());
  const defaultSince = toDatetimeLocal(new Date(nowRef.current.getTime() - 24 * 3600 * 1000).toISOString());
  const defaultUntil = toDatetimeLocal(nowRef.current.toISOString());

  const [customSince, setCustomSince] = useState(defaultSince);
  const [customUntil, setCustomUntil] = useState(defaultUntil);
  const [appliedSince, setAppliedSince] = useState(defaultSince);
  const [appliedUntil, setAppliedUntil] = useState(defaultUntil);
  const [customError, setCustomError] = useState<string | null>(null);

  const customRangeValid = range === "custom" && !!appliedSince && !!appliedUntil;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["monitor-latency", monitorId, range, range === "custom" ? `${appliedSince}:${appliedUntil}` : ""],
    queryFn: () =>
      range === "custom"
        ? getMonitorLatency(monitorId, range, fromDatetimeLocal(appliedSince), fromDatetimeLocal(appliedUntil))
        : getMonitorLatency(monitorId, range),
    enabled: range !== "custom" || customRangeValid,
    refetchInterval: range === "custom" ? false : 60_000,
  });

  const buckets: LatencyBucket[] = data?.buckets ?? [];
  const earliestDataAt = data?.earliest_data_at ?? null;

  const ageHours = earliestDataAt
    ? (Date.now() - new Date(earliestDataAt).getTime()) / (1000 * 60 * 60)
    : null;

  const notEnoughData =
    range !== "custom" &&
    ageHours !== null &&
    ageHours < MIN_AGE_HOURS[range as Exclude<TimeRange, "custom">];

  const p50Values = buckets.filter((b) => b.p50 != null).map((b) => b.p50 as number);
  const avgP50 = p50Values.length > 0 ? p50Values.reduce((a, b) => a + b, 0) / p50Values.length : null;
  const hasAnomalies = buckets.some(
    (b) => b.error_count > 0 || (b.p50 != null && (
      latencyThreshold != null
        ? b.p50 > latencyThreshold
        : avgP50 != null && b.p50 > avgP50 * 2.5
    ))
  );

  const customTooltip = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;
      const b = payload[0]?.payload as LatencyBucket;
      if (!b) return null;
      return (
        <div style={{
          background: "#121212",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 3,
          padding: "10px 14px",
          fontSize: 12,
          color: "#EFF2F5",
          minWidth: 190,
        }}>
          <div style={{ fontWeight: 700, color: "#EFF2F5", marginBottom: 6 }}>
            {new Date(label).toLocaleString(undefined, { timeZone: timezone })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {b.p50 != null && (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                <span style={{ color: "#98A1AB" }}>Median (P50)</span>
                <span style={{ fontWeight: 700, color: "#39D98A" }}>{b.p50}ms</span>
              </div>
            )}
            {b.p95 != null && (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                <span style={{ color: "#98A1AB" }}>P95</span>
                <span style={{ fontWeight: 600, color: "#FDDD48" }}>{b.p95}ms</span>
              </div>
            )}
            {b.p99 != null && (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                <span style={{ color: "#98A1AB" }}>P99</span>
                <span style={{ fontWeight: 600, color: "#FF5C5C" }}>{b.p99}ms</span>
              </div>
            )}
            {b.error_count > 0 && (
              <div style={{ color: "#FF5C5C", marginTop: 2 }}>
                {b.error_count} error{b.error_count > 1 ? "s" : ""} in this window
              </div>
            )}
            <div style={{ color: "#98A1AB", marginTop: 2 }}>
              {b.total_count} run{b.total_count !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      );
    },
    [timezone]
  );

  function applyCustomRange() {
    setCustomError(null);
    if (!customSince || !customUntil) {
      setCustomError("Both dates are required");
      return;
    }
    const sinceMs = new Date(customSince).getTime();
    const untilMs = new Date(customUntil).getTime();
    const widthMs = untilMs - sinceMs;
    if (widthMs < 30 * 60 * 1000) {
      setCustomError("Range must be at least 30 minutes");
      return;
    }
    if (widthMs > 30 * 24 * 3600 * 1000) {
      setCustomError("Range must not exceed 30 days");
      return;
    }
    setAppliedSince(customSince);
    setAppliedUntil(customUntil);
  }

  function renderBody() {
    if (range === "custom" && !customRangeValid) {
      return (
        <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#98A1AB", fontSize: 13 }}>
          Apply a date range to load data
        </div>
      );
    }
    if (isLoading) {
      return (
        <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#98A1AB", fontSize: 13 }}>
          Loading…
        </div>
      );
    }
    if (isError) {
      return (
        <div style={{ height: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <div style={{ color: "#EFF2F5", fontSize: 13 }}>Service temporarily unavailable</div>
          <div style={{ color: "#98A1AB", fontSize: 12, opacity: 0.7 }}>Unable to load chart data. Please try refreshing.</div>
        </div>
      );
    }
    if (notEnoughData) {
      return (
        <div style={{ height: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <div style={{ color: "#98A1AB", fontSize: 13 }}>Not enough data</div>
          <div style={{ color: "#98A1AB", fontSize: 12, opacity: 0.6 }}>
            {range === "30d" ? "Need at least 7 days of history" : "Need at least 24 hours of history"}
          </div>
        </div>
      );
    }
    if (buckets.length === 0) {
      return (
        <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#98A1AB", fontSize: 13 }}>
          No data for this time range
        </div>
      );
    }

    const interval = tickInterval(range, buckets.length);

    return (
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={buckets} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis
            dataKey="time"
            tickFormatter={(t) => formatTick(t, range, appliedSince, appliedUntil, timezone)}
            tick={{ fill: "#98A1AB", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            interval={interval}
          />
          <YAxis
            unit="ms"
            tick={{ fill: "#98A1AB", fontSize: 11 }}
            width={52}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={customTooltip} />
          <Line
            type="basis"
            dataKey="p50"
            stroke="#39D98A"
            strokeWidth={2}
            dot={(props) => <CustomDot {...props} avgP50={avgP50} latencyThreshold={latencyThreshold} />}
            activeDot={{ r: 5, fill: "#39D98A", stroke: "#121212", strokeWidth: 2 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "#EFF2F5" }}>{title}</h3>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: "#98A1AB" }}>
            Median response time across all probe regions
          </p>
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
          {(["1h", "24h", "7d", "30d", "custom"] as TimeRange[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              style={{
                padding: "4px 10px",
                fontSize: 12,
                border: "1px solid",
                borderColor: range === r ? "#39D98A" : "rgba(255,255,255,0.1)",
                background: range === r ? "rgba(57,217,138,0.1)" : "transparent",
                color: range === r ? "#39D98A" : "#98A1AB",
                borderRadius: 3,
                cursor: "pointer",
                fontWeight: range === r ? 700 : 400,
                fontFamily: "inherit",
              }}
            >
              {r === "custom" ? "Custom" : r}
            </button>
          ))}
        </div>
      </div>

      {range === "custom" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <input
            type="datetime-local"
            value={customSince}
            onChange={(e) => setCustomSince(e.target.value)}
            className="input-dark"
            style={{ fontSize: 12, padding: "4px 8px", borderRadius: 3, color: "#EFF2F5", background: "#121212" }}
          />
          <span style={{ color: "#98A1AB", fontSize: 12 }}>to</span>
          <input
            type="datetime-local"
            value={customUntil}
            onChange={(e) => setCustomUntil(e.target.value)}
            className="input-dark"
            style={{ fontSize: 12, padding: "4px 8px", borderRadius: 3, color: "#EFF2F5", background: "#121212" }}
          />
          <button
            onClick={applyCustomRange}
            className="btn-glass"
            style={{ fontSize: 12, padding: "4px 14px", borderRadius: 3, fontFamily: "inherit" }}
          >
            Apply
          </button>
          {customError && (
            <span style={{ fontSize: 12, color: "#FF5C5C" }}>{customError}</span>
          )}
        </div>
      )}

      {renderBody()}

      <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#98A1AB" }}>
          <div style={{ width: 20, height: 2, background: "#39D98A", borderRadius: 2 }} />
          Median (P50)
        </div>
        {hasAnomalies && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#98A1AB" }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FDDD48" }} />
              High latency
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#98A1AB" }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FF5C5C" }} />
              Error
            </div>
          </>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {(["p50", "p95", "p99"] as (keyof typeof PSTAT_INFO)[]).map((key) => (
            <div
              key={key}
              style={{ position: "relative" }}
              onMouseEnter={() => setShowInfo(key)}
              onMouseLeave={() => setShowInfo(null)}
            >
              <span style={{
                fontSize: 11, padding: "2px 7px",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 3, color: "#98A1AB",
                cursor: "default", fontWeight: 600, textTransform: "uppercase",
              }}>
                {key}
              </span>
              {showInfo === key && (
                <div style={{
                  position: "absolute",
                  bottom: "calc(100% + 8px)",
                  right: 0,
                  background: "#121212",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 3,
                  padding: "8px 12px",
                  fontSize: 12,
                  color: "#EFF2F5",
                  whiteSpace: "normal" as const,
                  zIndex: 20,
                  pointerEvents: "none",
                  maxWidth: 260,
                  lineHeight: 1.5,
                }}>
                  <strong style={{ color: "#EFF2F5" }}>{key.toUpperCase()}</strong> — {PSTAT_INFO[key]}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
